'use client'

import type React from 'react'

import { useState, useRef, useEffect } from 'react'
import { delay, motion, useAnimation } from 'framer-motion'
import { Card } from '@/Components/ui/card'
import { cn } from '@/lib/utils'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Link } from '@inertiajs/react'
import type { LanguageCode } from '@/types'
import { LanguageSensitiveLazySvg, LazySvg } from '../custom/lazy-svg'
import useLocalizedValue from '@/hooks/use-localized-value'
import { useTranslation } from 'react-i18next'

interface QuickNavItem {
  icon: string
  href: string | null
  name: { [key in LanguageCode]: string }
  order: number
}

interface QuickNavListProps {
  items?: QuickNavItem[]
  className?: string
  onSelect?: (item: QuickNavItem) => void
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24,
    },
  },
}

// Scroll indicator component with subtle animation
function ScrollIndicator({
  direction,
  isRtl,
  onClick,
}: {
  direction: 'start' | 'end'
  isRtl: boolean
  onClick: () => void
}) {
  // Determine which icon to use based on direction AND RTL mode
  const Icon = (() => {
    if (isRtl) {
      // In RTL: start = right, end = left
      return direction === 'start' ? ChevronRight : ChevronLeft
    } else {
      // In LTR: start = left, end = right
      return direction === 'start' ? ChevronLeft : ChevronRight
    }
  })()

  const controls = useAnimation()

  // Animation for the icon - direction depends on RTL and direction
  const startAnimation = () => {
    // Determine animation direction based on icon direction
    const animationDirection = (() => {
      if (isRtl) {
        return direction === 'start' ? 3 : -3 // RTL: start moves right, end moves left
      } else {
        return direction === 'start' ? -3 : 3 // LTR: start moves left, end moves right
      }
    })()

    controls.start({
      x: animationDirection,
      transition: {
        repeat: Number.POSITIVE_INFINITY,
        repeatType: 'reverse',
        duration: 1,
        ease: 'easeInOut',
      },
    })
  }

  // Start animation on mount
  useEffect(() => {
    startAnimation()
  }, [])

  return (
    <motion.div
      className="block cursor-pointer text-primary dark:text-white"
      onClick={onClick}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      onHoverStart={() => controls.stop()}
      onHoverEnd={startAnimation}
    >
      <motion.div animate={controls}>
        <Icon className="size-5" />
      </motion.div>
    </motion.div>
  )
}

// Internal CategoryCard component
function CategoryCard({ item, onClick }: { item: QuickNavItem; onClick: () => void }) {
  // Determine if we should use a Link or a div based on href
  const CardWrapper = ({ children }: { children: React.ReactNode }) => {
    if (item.href) {
      return (
        <Link href={item.href} className="block" onClick={onClick}>
          {children}
        </Link>
      )
    }
    return <div onClick={onClick}>{children}</div>
  }

  return (
    <CardWrapper key={item.order}>
      <Card className="group flex h-auto w-fit cursor-pointer flex-row items-center justify-center space-x-1 rounded-2xl border-border bg-transparent px-3 py-2 shadow-none transition-all duration-300 hover:border-primary hover:bg-primary hover:text-white sm:px-4 md:h-36 md:w-40 md:flex-col md:space-x-0 md:space-y-4 md:rounded-3xl md:p-4 lg:h-40 lg:w-48 rtl:space-x-reverse">
        <div className="rounded-full transition-transform duration-300 group-hover:scale-110">
          <LanguageSensitiveLazySvg
            name={item.icon}
            className="size-4 text-primary transition-colors duration-300 group-hover:text-white dark:text-foreground sm:size-5 md:size-8 lg:size-10 xl:size-12"
          ></LanguageSensitiveLazySvg>
        </div>
        <div className="max-w-[80px] whitespace-nowrap text-center text-xs font-semibold sm:max-w-[100px] sm:text-sm md:max-w-[120px] lg:max-w-full">
          {useLocalizedValue(item.name)}
        </div>
      </Card>
    </CardWrapper>
  )
}

export default function QuickNavList({ items = [], className, onSelect }: QuickNavListProps) {
  const [sortedItems, setSortedItems] = useState<Array<QuickNavItem & { key: string }>>([])
  const [canScroll, setCanScroll] = useState({ start: false, end: false })
  const [isScrollable, setIsScrollable] = useState(false)
  const { i18n } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const isRtl = i18n.dir() === 'rtl'
  const cardWidthRef = useRef<number>(0)

  // Handle item selection
  const handleItemSelect = (item: QuickNavItem) => {
    if (onSelect) onSelect(item)
  }

  // Update scroll indicators and check if scrolling is needed
  const updateScrollState = () => {
    const el = scrollRef.current
    if (!el) return

    const { scrollLeft, scrollWidth, clientWidth } = el

    // Check if content is wider than container (needs scrolling)
    const needsScrolling = scrollWidth > clientWidth + 5 // Small buffer for rounding errors
    setIsScrollable(needsScrolling)

    // Update scroll button states
    if (needsScrolling) {
      if (isRtl) {
        // In RTL mode, scrollLeft is negative and starts from the right
        // We need to convert to positive for easier comparison
        const rtlScrollLeft = Math.abs(scrollLeft)
        const maxScroll = scrollWidth - clientWidth

        setCanScroll({
          // In RTL, start is right side (opposite of LTR)
          start: rtlScrollLeft < maxScroll - 10,
          // In RTL, end is left side (opposite of LTR)
          end: rtlScrollLeft > 10,
        })
      } else {
        // Standard LTR logic
        setCanScroll({
          start: scrollLeft > 10,
          end: scrollLeft < scrollWidth - clientWidth - 10,
        })
      }
    } else {
      setCanScroll({ start: false, end: false })
    }
  }

  // Measure card width for better scrolling
  useEffect(() => {
    const el = scrollRef.current
    if (!el || !el.children.length) return

    // Get the width of the first card (including gap)
    const firstCard = el.children[0] as HTMLElement
    if (firstCard) {
      // Get the computed style to account for gap
      const style = window.getComputedStyle(el)
      const gap = Number.parseInt(style.getPropertyValue('gap') || '0', 3)

      // Store card width + gap for scrolling
      cardWidthRef.current = firstCard.offsetWidth + gap
    }
  }, [sortedItems])

  // Set up scroll event listener
  useEffect(() => {
    const sorted = Object.entries(items)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([key, item]) => ({ key, ...item }))
    setSortedItems(sorted)

    const el = scrollRef.current
    if (!el) return

    // Initial check
    updateScrollState()

    // Check on scroll
    el.addEventListener('scroll', updateScrollState, { passive: true })

    // Check on resize
    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(el)

    return () => {
      el.removeEventListener('scroll', updateScrollState)
      resizeObserver.disconnect()
    }
  }, [items, isRtl])

  // Simplified smooth scrolling function
  const scroll = (direction: 'start' | 'end') => {
    const el = scrollRef.current
    if (!el) return

    // Calculate scroll amount based on card width or container width
    let scrollAmount: number

    // If we have measured card width, scroll by 2 cards
    if (cardWidthRef.current > 0) {
      scrollAmount = cardWidthRef.current * 0.5
    } else {
      // Fallback to percentage of container width
      scrollAmount = el.clientWidth * 0.5
    }

    // Determine scroll direction based on RTL/LTR and start/end
    const scrollDirection = isRtl
      ? direction === 'start'
        ? scrollAmount
        : -scrollAmount
      : direction === 'start'
        ? -scrollAmount
        : scrollAmount

    // Use the built-in smooth scrolling
    el.scrollBy({
      left: scrollDirection,
      behavior: 'smooth',
    })
  }

  return (
    <motion.div
      className={cn('relative', className)}
      initial="hidden"
      animate="visible"
      whileInView="visible"
      viewport={{ once: true, margin: '-100px' }}
      variants={containerVariants}
      dir={i18n.dir()}
    >
      {/* Main container with the correct structure */}
      <div className="flex items-center justify-center">
        {/* Start indicator - only visible when scrolling is needed AND can scroll in that direction */}
        {isScrollable && canScroll.start && (
          <div className="flex-shrink-0 self-center pb-3">
            <ScrollIndicator
              direction="start"
              isRtl={isRtl}
              onClick={() => scroll(isRtl ? 'end' : 'start')}
            />
          </div>
        )}

        {/* Scrollable items container */}
        <div
          ref={scrollRef}
          className={cn(
            'scrollbar-hide flex items-center gap-2 overflow-x-auto scroll-smooth pb-4 md:gap-4',
            !isScrollable && 'justify-center'
          )}
          style={{ scrollSnapType: 'x mandatory', scrollbarGutter: 'stable both-edges' }}
        >
          {/* Main content items */}
          {sortedItems.map((item, index) => (
            <motion.div
              key={`${item.order}-${index}`}
              variants={itemVariants}
              className="shrink-0 snap-center"
            >
              <CategoryCard key={item.href} item={item} onClick={() => handleItemSelect(item)} />
            </motion.div>
          ))}
        </div>

        {/* End indicator - only visible when scrolling is needed AND can scroll in that direction */}
        {isScrollable && canScroll.end && (
          <div className="flex-shrink-0 self-center pb-3">
            <ScrollIndicator
              direction="end"
              isRtl={isRtl}
              onClick={() => scroll(isRtl ? 'start' : 'end')}
            />
          </div>
        )}
      </div>
    </motion.div>
  )
}
